import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Camera, X } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReviewForm = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [form, setForm] = useState({ name: "", location: "", text: "" });
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Photo must be under 8MB.");
      return;
    }
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  };

  const clearPhoto = () => {
    setPhoto(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
  };

  const reset = () => {
    setForm({ name: "", location: "", text: "" });
    setRating(5);
    clearPhoto();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      toast.error("Please add your name and a short review.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("location", form.location);
      fd.append("text", form.text);
      fd.append("rating", String(rating));
      if (photo) fd.append("photo", photo);
      await axios.post(`${API}/reviews`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Thanks! Your review is now live on the wall.");
      reset();
      setOpen(false);
      onSuccess?.();
    } catch (err) {
      toast.error("Couldn't post your review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="open-review-form"
          className="group flex items-center gap-2 bg-transparent text-[var(--tt-green)] border-2 border-[var(--tt-green)] font-display uppercase text-sm md:text-base px-6 py-3 hover:bg-[var(--tt-green)] hover:text-black transition-[background-color,color] duration-300 tt-clip-card"
        >
          <Star weight="fill" className="h-4 w-4" />
          Leave a Review
        </button>
      </DialogTrigger>
      <DialogContent
        data-testid="review-form-dialog"
        className="bg-[var(--tt-surface)] border-white/10 text-white max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase text-2xl tracking-tight text-left">
            Share your <span className="text-[var(--tt-green)]">experience</span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} data-testid="review-form" className="flex flex-col gap-4 mt-2">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                type="button"
                key={n}
                data-testid={`review-star-${n}`}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setRating(n)}
                className="p-1"
                aria-label={`${n} star`}
              >
                <Star
                  weight="fill"
                  className={`h-7 w-7 transition-colors ${
                    n <= (hover || rating) ? "text-[var(--tt-green)]" : "text-zinc-700"
                  }`}
                />
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              data-testid="review-input-name"
              value={form.name}
              onChange={update("name")}
              placeholder="Your name"
              className="bg-black/50 border border-white/10 px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[var(--tt-green)] focus:outline-none transition-colors"
            />
            <input
              data-testid="review-input-location"
              value={form.location}
              onChange={update("location")}
              placeholder="City (optional)"
              className="bg-black/50 border border-white/10 px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[var(--tt-green)] focus:outline-none transition-colors"
            />
          </div>

          <textarea
            data-testid="review-input-text"
            rows={4}
            value={form.text}
            onChange={update("text")}
            placeholder="Tell us how the tow went…"
            className="bg-black/50 border border-white/10 px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[var(--tt-green)] focus:outline-none resize-none transition-colors"
          />

          {preview ? (
            <div className="relative w-full h-40 overflow-hidden border border-white/10">
              <img src={preview} alt="preview" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={clearPhoto}
                data-testid="review-remove-photo"
                className="absolute top-2 right-2 bg-black/70 p-1.5 hover:bg-[var(--tt-danger)] transition-colors"
              >
                <X weight="bold" className="h-4 w-4 text-white" />
              </button>
            </div>
          ) : (
            <label
              data-testid="review-photo-label"
              className="flex items-center justify-center gap-3 border border-dashed border-white/20 py-6 cursor-pointer hover:border-[var(--tt-green)] hover:text-[var(--tt-green)] text-zinc-400 transition-colors"
            >
              <Camera weight="fill" className="h-6 w-6" />
              <span className="font-mono text-xs uppercase tracking-widest">Add a photo of the tow</span>
              <input
                type="file"
                accept="image/*"
                onChange={onFile}
                data-testid="review-input-photo"
                className="hidden"
              />
            </label>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="review-submit"
            className="mt-1 bg-[var(--tt-green)] text-black font-display uppercase text-lg px-8 py-3.5 border-2 border-[var(--tt-green)] hover:bg-transparent hover:text-[var(--tt-green)] transition-[background-color,color] duration-300 disabled:opacity-60 tt-clip-card"
          >
            {submitting ? "Posting…" : "Post Review"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
